/**
 * Material library — one definition drives BOTH how a body renders (PBR
 * appearance) and how it computes (mass properties today, FEA tomorrow).
 *
 * Physical properties are room-temperature engineering values for the most
 * common grade of each material (6061-T6, 304, Ti-6Al-4V, printed PLA…).
 * They are the contract the FEA feature will consume: linear-elastic
 * isotropic (E, ν, ρ) plus strength limits for factor-of-safety readouts.
 *
 * Assignment lives on the FeatureNode (`material` field) OUTSIDE the
 * evaluator's content hash — swapping a material re-tints the render and
 * re-weighs mass, but never re-executes kernel geometry.
 */

export interface MaterialAppearance {
  /** Base color, #rrggbb. */
  readonly color: string;
  readonly metalness: number;
  readonly roughness: number;
  /** < 1 renders transparent (acrylic, glass). */
  readonly opacity?: number;
  /** Procedural roughness texture: directional streaks or fine speckle. */
  readonly texture?: 'brushed' | 'speckle';
}

export interface MaterialPhysical {
  /** g/cm³ — mass = volume(mm³)/1000 × density. */
  readonly density: number;
  /** Young's modulus, GPa. */
  readonly youngsModulus: number;
  /** Poisson's ratio (0..0.5). */
  readonly poissonRatio: number;
  /** Yield strength, MPa (0.2% offset; flexural for brittle materials). */
  readonly yieldStrength: number;
  /** Ultimate tensile strength, MPa. */
  readonly ultimateStrength: number;
  /** Coefficient of thermal expansion, µm/(m·K). */
  readonly thermalExpansion: number;
}

export interface MaterialDef {
  readonly id: string;
  readonly name: string;
  readonly category: 'polymer' | 'metal' | 'glass' | 'elastomer';
  readonly appearance: MaterialAppearance;
  readonly physical: MaterialPhysical;
}

export const MATERIALS: readonly MaterialDef[] = [
  // --- printable / molded polymers -----------------------------------------
  {
    id: 'pla', name: 'PLA', category: 'polymer',
    appearance: { color: '#e8e4dc', metalness: 0, roughness: 0.55, texture: 'speckle' },
    physical: { density: 1.24, youngsModulus: 3.5, poissonRatio: 0.36, yieldStrength: 60, ultimateStrength: 65, thermalExpansion: 68 },
  },
  {
    id: 'abs', name: 'ABS', category: 'polymer',
    appearance: { color: '#d9d3c8', metalness: 0, roughness: 0.5 },
    physical: { density: 1.05, youngsModulus: 2.3, poissonRatio: 0.35, yieldStrength: 40, ultimateStrength: 45, thermalExpansion: 90 },
  },
  {
    id: 'petg', name: 'PETG', category: 'polymer',
    appearance: { color: '#cfd8d4', metalness: 0, roughness: 0.35 },
    physical: { density: 1.27, youngsModulus: 2.1, poissonRatio: 0.37, yieldStrength: 50, ultimateStrength: 53, thermalExpansion: 60 },
  },
  {
    id: 'nylon-12', name: 'Nylon 12', category: 'polymer',
    appearance: { color: '#e3e3df', metalness: 0, roughness: 0.6, texture: 'speckle' },
    physical: { density: 1.01, youngsModulus: 1.7, poissonRatio: 0.39, yieldStrength: 45, ultimateStrength: 48, thermalExpansion: 100 },
  },
  {
    id: 'polycarbonate', name: 'Polycarbonate', category: 'polymer',
    appearance: { color: '#dfe6ea', metalness: 0, roughness: 0.25, opacity: 0.55 },
    physical: { density: 1.2, youngsModulus: 2.4, poissonRatio: 0.37, yieldStrength: 62, ultimateStrength: 66, thermalExpansion: 65 },
  },
  {
    id: 'acrylic', name: 'Acrylic (PMMA)', category: 'polymer',
    appearance: { color: '#e9f2f5', metalness: 0, roughness: 0.08, opacity: 0.25 },
    physical: { density: 1.18, youngsModulus: 3.0, poissonRatio: 0.37, yieldStrength: 65, ultimateStrength: 70, thermalExpansion: 75 },
  },
  {
    id: 'tpu-95a', name: 'TPU 95A', category: 'elastomer',
    appearance: { color: '#4a4d52', metalness: 0, roughness: 0.85, texture: 'speckle' },
    physical: { density: 1.21, youngsModulus: 0.03, poissonRatio: 0.48, yieldStrength: 8, ultimateStrength: 30, thermalExpansion: 150 },
  },
  // --- metals ----------------------------------------------------------------
  {
    id: 'aluminum-6061', name: 'Aluminum 6061-T6', category: 'metal',
    appearance: { color: '#c8cdd4', metalness: 0.95, roughness: 0.4, texture: 'brushed' },
    physical: { density: 2.7, youngsModulus: 68.9, poissonRatio: 0.33, yieldStrength: 276, ultimateStrength: 310, thermalExpansion: 23.6 },
  },
  {
    id: 'steel-mild', name: 'Mild steel (A36)', category: 'metal',
    appearance: { color: '#9aa0a8', metalness: 0.9, roughness: 0.5, texture: 'brushed' },
    physical: { density: 7.85, youngsModulus: 200, poissonRatio: 0.29, yieldStrength: 250, ultimateStrength: 400, thermalExpansion: 12 },
  },
  {
    id: 'stainless-304', name: 'Stainless 304', category: 'metal',
    appearance: { color: '#c2c7cc', metalness: 0.95, roughness: 0.3, texture: 'brushed' },
    physical: { density: 8.0, youngsModulus: 193, poissonRatio: 0.29, yieldStrength: 215, ultimateStrength: 505, thermalExpansion: 17.3 },
  },
  {
    id: 'brass', name: 'Brass C360', category: 'metal',
    appearance: { color: '#cfa952', metalness: 0.95, roughness: 0.35 },
    physical: { density: 8.5, youngsModulus: 97, poissonRatio: 0.31, yieldStrength: 310, ultimateStrength: 400, thermalExpansion: 20.5 },
  },
  {
    id: 'copper', name: 'Copper C110', category: 'metal',
    appearance: { color: '#c67f4e', metalness: 0.95, roughness: 0.3 },
    physical: { density: 8.94, youngsModulus: 117, poissonRatio: 0.34, yieldStrength: 70, ultimateStrength: 220, thermalExpansion: 17 },
  },
  {
    id: 'titanium-gr5', name: 'Titanium Ti-6Al-4V', category: 'metal',
    appearance: { color: '#a7abb3', metalness: 0.9, roughness: 0.45, texture: 'brushed' },
    physical: { density: 4.43, youngsModulus: 113.8, poissonRatio: 0.342, yieldStrength: 880, ultimateStrength: 950, thermalExpansion: 8.6 },
  },
  // --- glass -----------------------------------------------------------------
  {
    id: 'glass', name: 'Glass (soda-lime)', category: 'glass',
    appearance: { color: '#d7e8e4', metalness: 0, roughness: 0.02, opacity: 0.18 },
    physical: { density: 2.5, youngsModulus: 70, poissonRatio: 0.22, yieldStrength: 33, ultimateStrength: 33, thermalExpansion: 9 },
  },
];

const BY_ID = new Map(MATERIALS.map((m) => [m.id, m]));

export function materialById(id: string | undefined): MaterialDef | undefined {
  return id === undefined ? undefined : BY_ID.get(id);
}

/** Mass in grams from an exact volume in mm³ (density is g/cm³). */
export function massOf(volumeMm3: number, material: MaterialDef): number {
  return (volumeMm3 / 1000) * material.physical.density;
}

/**
 * Generic material-chain resolution (shared by the live document and the
 * assembly sub-evaluator): a node's own assignment, else the nearest
 * consumed ancestor's; instances follow their source. Material and color
 * resolve independently — a color-only override tints the inherited
 * material rather than replacing it.
 */
export function resolveMaterialChain(
  getNode: (id: string) =>
    | {
        material?: string;
        materialColor?: string;
        type?: string;
        params?: unknown;
      }
    | undefined,
  getResult: (id: string) =>
    | {
        kind: string;
        consumed?: readonly string[];
        componentMaterials?: readonly { material?: string; color?: string; volume: number }[];
      }
    | undefined,
  id: string,
): { material?: MaterialDef; color?: string } | null {
  const seen = new Set<string>();
  let color: string | undefined;
  const walk = (fid: string): MaterialDef | undefined => {
    if (seen.has(fid)) return undefined;
    seen.add(fid);
    const node = getNode(fid);
    if (color === undefined && node?.materialColor !== undefined) color = node.materialColor;
    const def = materialById(node?.material);
    if (def) return def;
    const r = getResult(fid);
    if (r?.kind === 'body') {
      if (r.consumed) {
        for (const c of r.consumed) {
          const hit = walk(c);
          if (hit) return hit;
        }
      }
      // Part features: fall back to the DOMINANT source component — largest
      // volume with a material, and INDEPENDENTLY the largest with a color
      // (a color-only source part must keep its tint in the assembly).
      if (r.componentMaterials?.length) {
        const byVolume = [...r.componentMaterials].sort((a, b) => b.volume - a.volume);
        const domColor = byVolume.find((c) => c.color !== undefined);
        if (color === undefined && domColor) color = domColor.color;
        const domMat = byVolume.find((c) => materialById(c.material));
        if (domMat) return materialById(domMat.material);
      }
    }
    // Instances render copies of their source WITHOUT consuming it.
    if (node?.type === 'instance') {
      const src = (node.params as { source?: { $feature?: string } } | undefined)?.source?.$feature;
      if (src) return walk(src);
    }
    return undefined;
  };
  const material = walk(id);
  if (!material && color === undefined) return null;
  return { ...(material ? { material } : {}), ...(color !== undefined ? { color } : {}) };
}
